import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, CheckCircle2, File as FileIcon, Image as ImageIcon, Info, Music, UploadCloud, Video, X } from 'lucide-react'
import { Dialog, ScrollableDialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/scrollable-dialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  acceptAttribute,
  attachmentRule,
  checkAttachment,
  formatBytes,
  MAX_ATTACHMENTS_PER_MESSAGE,
  type AttachmentChannel,
  type AttachmentKind,
} from '@/lib/inbox/attachmentRules'

const KIND_TITLE: Record<AttachmentKind, string> = {
  image: 'Send image',
  video: 'Send video',
  audio: 'Send audio',
  document: 'Send document',
}

const KIND_ICON: Record<AttachmentKind, React.ComponentType<{ className?: string }>> = {
  image: ImageIcon,
  video: Video,
  audio: Music,
  document: FileIcon,
}

const CHANNEL_NAME: Record<AttachmentChannel, string> = {
  whatsapp: 'WhatsApp',
  instagram: 'Instagram',
  facebook: 'Facebook',
  teamchat: 'Team chat',
}

interface PickedFile {
  id: string
  file: File
  ok: boolean
  reason: string | null
  previewUrl: string | null
}

export interface AttachMediaDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  channel: AttachmentChannel
  kind: AttachmentKind
  alreadyAttached: number
  onAdd: (files: File[]) => void
}

const fileKey = (f: File) => `${f.name}:${f.size}:${f.lastModified}`

export function AttachMediaDialog({ open, onOpenChange, channel, kind, alreadyAttached, onAdd }: AttachMediaDialogProps) {
  const rule = attachmentRule(channel, kind)
  const [picked, setPicked] = useState<PickedFile[]>([])
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const pickedRef = useRef<PickedFile[]>([])
  pickedRef.current = picked

  const slots = Math.max(0, MAX_ATTACHMENTS_PER_MESSAGE - alreadyAttached)
  const allowMultiple = !!rule?.multiple && slots > 1

  const revokeAll = useCallback((list: PickedFile[]) => {
    for (const p of list) if (p.previewUrl) URL.revokeObjectURL(p.previewUrl)
  }, [])

  useEffect(() => {
    if (!open) {
      revokeAll(pickedRef.current)
      setPicked([])
      setDragging(false)
    }
  }, [open, revokeAll])

  useEffect(() => () => revokeAll(pickedRef.current), [revokeAll])

  const addFiles = useCallback((files: File[]) => {
    if (!files.length) return
    const prev = pickedRef.current
    let base = prev
    let incoming = files
    if (!allowMultiple) {
      revokeAll(prev)
      base = []
      incoming = files.slice(0, 1)
    }
    const seen = new Set(base.map((p) => fileKey(p.file)))
    let acceptedCount = base.filter((p) => p.ok).length
    const next = [...base]
    for (const file of incoming) {
      const key = fileKey(file)
      if (seen.has(key)) continue
      seen.add(key)
      const check = checkAttachment(file, channel, kind)
      let ok = check.ok
      let reason = check.ok ? null : check.reason
      if (ok && acceptedCount >= slots) {
        ok = false
        reason = slots === 0
          ? `This message already has ${MAX_ATTACHMENTS_PER_MESSAGE} attachments.`
          : `You can attach up to ${MAX_ATTACHMENTS_PER_MESSAGE} files per message.`
      }
      if (ok) acceptedCount += 1
      const previewUrl = ok && kind === 'image' ? URL.createObjectURL(file) : null
      next.push({ id: `${key}:${next.length}`, file, ok, reason, previewUrl })
    }
    pickedRef.current = next
    setPicked(next)
  }, [allowMultiple, channel, kind, revokeAll, slots])

  const remove = (id: string) => {
    const prev = pickedRef.current
    const gone = prev.find((p) => p.id === id)
    if (gone?.previewUrl) URL.revokeObjectURL(gone.previewUrl)
    const next = prev.filter((p) => p.id !== id)
    pickedRef.current = next
    setPicked(next)
  }

  useEffect(() => {
    if (!open) return
    const onPaste = (e: ClipboardEvent) => {
      const files = Array.from(e.clipboardData?.files || [])
      if (!files.length) return
      e.preventDefault()
      addFiles(files)
    }
    document.addEventListener('paste', onPaste)
    return () => document.removeEventListener('paste', onPaste)
  }, [open, addFiles])

  const accepted = useMemo(() => picked.filter((p) => p.ok), [picked])
  const rejectedCount = picked.length - accepted.length

  const confirm = () => {
    if (!accepted.length) return
    onAdd(accepted.map((p) => p.file))
    onOpenChange(false)
  }

  if (!rule) return null
  const Icon = KIND_ICON[kind]
  const limitLine = allowMultiple
    ? `Up to ${Math.min(slots, MAX_ATTACHMENTS_PER_MESSAGE)} files, ${formatBytes(rule.maxBytes)} each`
    : `One file, up to ${formatBytes(rule.maxBytes)}`

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <ScrollableDialogContent maxWidthXs="560px" data-testid="attach-media-dialog">
        <div
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon className="h-5 w-5" />
              {KIND_TITLE[kind]}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">Sending on {CHANNEL_NAME[channel]}</p>
          </DialogHeader>

          <div
            role="button"
            tabIndex={0}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                inputRef.current?.click()
              }
            }}
            onDragOver={(e) => {
              e.preventDefault()
              if (!dragging) setDragging(true)
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false)
            }}
            onDrop={(e) => {
              e.preventDefault()
              setDragging(false)
              addFiles(Array.from(e.dataTransfer?.files || []))
            }}
            className={cn(
              'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
              dragging ? 'border-primary bg-primary/5' : 'border-border bg-muted/40 hover:border-primary/60 hover:bg-muted',
            )}
            data-testid="attach-media-dropzone"
          >
            <UploadCloud className={cn('h-8 w-8', dragging ? 'text-primary' : 'text-muted-foreground')} />
            <p className="text-sm font-medium text-foreground">
              {dragging ? 'Drop to add' : <>Drag and drop {allowMultiple ? 'files' : 'a file'} here, or <span className="text-primary">browse</span></>}
            </p>
            <p className="text-xs text-muted-foreground">You can also paste {allowMultiple ? 'files' : 'a file'}.</p>
            <input
              ref={inputRef}
              type="file"
              accept={acceptAttribute(rule)}
              multiple={allowMultiple}
              className="hidden"
              onChange={(e) => {
                addFiles(Array.from(e.target.files || []))
                e.target.value = ''
              }}
              data-testid="attach-media-input"
            />
          </div>

          <div className="rounded-xl border border-border bg-muted/40 p-3 text-sm" data-testid="attach-media-rules">
            <div className="mb-2 flex items-center gap-1.5 font-medium text-foreground">
              <Info className="h-4 w-4 text-muted-foreground" />
              What you can send
            </div>
            <dl className="grid grid-cols-[96px_1fr] gap-x-3 gap-y-1 text-[13px]">
              <dt className="text-muted-foreground">Formats</dt>
              <dd className="text-foreground" data-testid="attach-media-formats">{rule.formats.join(', ')}</dd>
              <dt className="text-muted-foreground">Size</dt>
              <dd className="text-foreground" data-testid="attach-media-limit">{limitLine}</dd>
            </dl>
            {rule.notes.length > 0 && (
              <ul className="mt-2 list-disc space-y-0.5 pl-5 text-[12px] text-muted-foreground">
                {rule.notes.map((n) => <li key={n}>{n}</li>)}
              </ul>
            )}
            {slots < MAX_ATTACHMENTS_PER_MESSAGE && slots > 0 && (
              <p className="mt-2 text-[12px] text-muted-foreground">
                {alreadyAttached} already attached — {slots} more can be added.
              </p>
            )}
          </div>

          {picked.length > 0 && (
            <ul className="space-y-1.5" data-testid="attach-media-list">
              {picked.map((p) => (
                <li
                  key={p.id}
                  className={cn(
                    'flex items-center gap-3 rounded-lg border px-3 py-2',
                    p.ok ? 'border-border bg-background' : 'border-destructive/30 bg-destructive/5',
                  )}
                  data-testid={p.ok ? 'attach-media-accepted' : 'attach-media-rejected'}
                >
                  {p.previewUrl ? (
                    <img src={p.previewUrl} alt="" className="h-10 w-10 shrink-0 rounded-md object-cover" />
                  ) : (
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground" title={p.file.name}>{p.file.name}</p>
                    {p.ok ? (
                      <p className="flex items-center gap-1 text-xs text-muted-foreground">
                        <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                        {formatBytes(p.file.size)}
                      </p>
                    ) : (
                      <p className="flex items-start gap-1 text-xs text-destructive">
                        <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" />
                        <span>{p.reason}</span>
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(p.id)}
                    aria-label={`Remove ${p.file.name}`}
                    className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {rejectedCount > 0 && accepted.length > 0 && (
            <p className="text-xs text-muted-foreground">Files that can't be sent are skipped.</p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={confirm} disabled={accepted.length === 0} data-testid="attach-media-add">
              {accepted.length > 1 ? `Add ${accepted.length} files` : 'Add to message'}
            </Button>
          </DialogFooter>
        </div>
      </ScrollableDialogContent>
    </Dialog>
  )
}
